/**
 * Auto Lossback Page (คืนยอดเสียอัตโนมัติ)
 * 
 * หน้าจัดการตั้งค่าคืนยอดเสียอัตโนมัติ
 * - สร้าง/แก้ไข Config
 * - เปิด/ปิด Config
 * - ทดสอบก่อนรัน
 * - รันทันที (Manual Execute)
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  RefreshCcw,
  Plus,
  Settings,
  Play,
  Eye,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Calendar,
  Gamepad2,
  Percent,
  Banknote,
  Zap,
  Edit,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth/auth-provider'
import {
  AutoLossbackService,
  type LossbackConfig,
  type LossbackConfigStats,
  type CreateLossbackConfigData,
  type LossbackPreviewResult,
  type LossbackPeriod,
} from '@/domain/auto-lossback'
import { GAME_GROUPS } from '@/domain/return-lose'

// ============================================
// Helper Functions
// ============================================

function formatNumber(value: number, decimals: number = 0): string {
  return value.toLocaleString('th-TH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function formatDate(date: Date | string | undefined): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getPeriodLabel(period: LossbackPeriod): string {
  switch (period) {
    case 'daily': return 'รายวัน'
    case 'weekly': return 'รายสัปดาห์'
    case 'semi_monthly': return 'รายครึ่งเดือน'
    case 'monthly': return 'รายเดือน'
    default: return period
  }
}

function getPeriodBadge(period: LossbackPeriod) {
  switch (period) {
    case 'daily':
      return <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/30">รายวัน</Badge>
    case 'weekly':
      return <Badge className="bg-purple-500/15 text-purple-600 border-purple-500/30">รายสัปดาห์</Badge>
    case 'semi_monthly':
      return <Badge className="bg-teal-500/15 text-teal-600 border-teal-500/30">รายครึ่งเดือน</Badge>
    case 'monthly':
      return <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">รายเดือน</Badge>
    default:
      return <Badge variant="secondary">{period}</Badge>
  }
}

// ============================================
// Stats Cards Component
// ============================================

function StatsCards({ stats, isLoading }: { stats: LossbackConfigStats | null; isLoading: boolean }) {
  const cards = [
    { label: 'รายการทั้งหมด', value: stats?.total || 0, icon: Settings, color: 'text-blue-600', bg: 'bg-blue-500/10' },
    { label: 'เปิดใช้งาน', value: stats?.active || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-500/10' },
    { label: 'ปิดใช้งาน', value: stats?.inactive || 0, icon: XCircle, color: 'text-zinc-500', bg: 'bg-zinc-500/10' },
    { label: 'รันวันนี้', value: stats?.todayRuns || 0, icon: Zap, color: 'text-pink-600', bg: 'bg-pink-500/10' },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <Card key={card.label} className="border-zinc-200/50 dark:border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn("p-2.5 rounded-xl", card.bg)}>
                <card.icon className={cn("w-5 h-5", card.color)} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{card.label}</p>
                {isLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-lg font-bold mt-0.5">{card.value.toLocaleString()}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ============================================
// Create/Edit Dialog Component
// ============================================

function ConfigDialog({
  open,
  onClose,
  onSuccess,
  editConfig,
}: {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  editConfig?: LossbackConfig | null
}) {
  const { sessionToken } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedGames, setSelectedGames] = useState<string[]>(['slot'])
  const [percentReturn, setPercentReturn] = useState(5)
  const [amountLoseMin, setAmountLoseMin] = useState(100)
  const [afterBonus, setAfterBonus] = useState(true)
  const [returnBonus, setReturnBonus] = useState(false)
  const [returnBonusTurnover, setReturnBonusTurnover] = useState(1)
  const [period, setPeriod] = useState<LossbackPeriod>('daily')
  const [autoApprove, setAutoApprove] = useState(false)
  const [notifyMember, setNotifyMember] = useState(true)
  const [isActive, setIsActive] = useState(true)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (editConfig) {
        setName(editConfig.name)
        setDescription(editConfig.description || '')
        setSelectedGames(editConfig.game_groups || ['slot'])
        setPercentReturn(editConfig.percent_return)
        setAmountLoseMin(editConfig.amount_lose_min)
        setAfterBonus(editConfig.after_bonus)
        setReturnBonus(editConfig.return_bonus)
        setReturnBonusTurnover(editConfig.return_bonus_turnover)
        setPeriod(editConfig.period)
        setAutoApprove(editConfig.auto_approve)
        setNotifyMember(editConfig.notify_member ?? true)
        setIsActive(editConfig.is_active)
      } else {
        setName('')
        setDescription('')
        setSelectedGames(['slot'])
        setPercentReturn(5)
        setAmountLoseMin(100)
        setAfterBonus(true)
        setReturnBonus(false)
        setReturnBonusTurnover(1)
        setPeriod('daily')
        setAutoApprove(false)
        setNotifyMember(true)
        setIsActive(true)
      }
    }
  }, [open, editConfig])

  const toggleGame = (gameId: string) => {
    setSelectedGames(prev =>
      prev.includes(gameId)
        ? prev.filter(g => g !== gameId)
        : [...prev, gameId]
    )
  }

  const handleSubmit = async () => {
    if (!sessionToken || !name || selectedGames.length === 0) return

    setIsSubmitting(true)
    try {
      const data: CreateLossbackConfigData = {
        name,
        description,
        game_groups: selectedGames,
        percent_return: percentReturn,
        amount_lose_min: amountLoseMin,
        after_bonus: afterBonus,
        return_bonus: returnBonus,
        return_bonus_turnover: returnBonusTurnover,
        period,
        auto_approve: autoApprove,
        notify_member: notifyMember,
        is_active: isActive,
      }

      if (editConfig) {
        // อัพเดท config ที่มีอยู่
        await AutoLossbackService.updateConfig(sessionToken, { id: editConfig.objectId, ...data })
      } else {
        // สร้าง config ใหม่
        await AutoLossbackService.createConfig(sessionToken, data)
      }
      
      onSuccess()
      onClose()
    } catch (err: any) {
      console.error('Error saving config:', err)
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 flex items-center justify-center">
              <Settings className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <span className="text-lg">{editConfig ? 'แก้ไข' : 'สร้าง'} Config คืนยอดอัตโนมัติ</span>
              <DialogDescription className="mt-1">
                กำหนดเงื่อนไขและตารางเวลาสำหรับคืนยอดเสียอัตโนมัติ
              </DialogDescription>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>ชื่อ Config <span className="text-red-500">*</span></Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="เช่น คืนยอดเสีย Slot ทุกวัน 5%"
              />
            </div>
            <div className="space-y-2">
              <Label>คำอธิบาย</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="รายละเอียดเพิ่มเติม..."
                rows={2}
              />
            </div>
          </div>

          {/* Game Selection */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Gamepad2 className="w-4 h-4 text-blue-500" />
              กลุ่มเกมที่คืนยอดเสีย <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {GAME_GROUPS.map((game) => (
                <label
                  key={game.value}
                  className={cn(
                    "flex items-center gap-2 p-2.5 border rounded-lg cursor-pointer transition-all text-sm",
                    "hover:bg-muted/50 hover:border-primary",
                    selectedGames.includes(game.value) && "border-primary bg-primary/5"
                  )}
                >
                  <Checkbox
                    checked={selectedGames.includes(game.value)}
                    onCheckedChange={() => toggleGame(game.value)}
                  />
                  <span className="truncate">{game.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Amount & Percent */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Banknote className="w-4 h-4 text-amber-500" />
                ยอดเสียขั้นต่ำ (บาท) <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={amountLoseMin}
                onChange={(e) => setAmountLoseMin(Number(e.target.value))}
                placeholder="100"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Percent className="w-4 h-4 text-pink-500" />
                เปอร์เซ็นต์คืน <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={percentReturn}
                onChange={(e) => setPercentReturn(Number(e.target.value))}
                placeholder="5"
              />
            </div>
          </div>

          {/* Period */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-500" />
              รอบการทำงาน <span className="text-red-500">*</span>
            </Label>
            <Select value={period} onValueChange={(v) => setPeriod(v as LossbackPeriod)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">รายวัน (ทุกวัน)</SelectItem>
                <SelectItem value="weekly">รายสัปดาห์ (วันจันทร์)</SelectItem>
                <SelectItem value="semi_monthly">รายครึ่งเดือน (วันที่ 1 และ 16)</SelectItem>
                <SelectItem value="monthly">รายเดือน (วันที่ 1)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {period === 'daily' && 'คำนวณจากยอดเสียของเมื่อวาน แล้วคืนให้ทุกวัน'}
              {period === 'weekly' && 'คำนวณจากยอดเสียของสัปดาห์ก่อน (จันทร์-อาทิตย์) แล้วคืนให้ทุกวันจันทร์'}
              {period === 'semi_monthly' && 'คำนวณจากยอดเสียครึ่งเดือน: 1-15 → รันวันที่ 16, 16-สิ้นเดือน → รันวันที่ 1'}
              {period === 'monthly' && 'คำนวณจากยอดเสียของเดือนก่อน (1-สิ้นเดือน) แล้วคืนให้ทุกวันที่ 1'}
            </p>
          </div>

          {/* Options */}
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <Label>หักโบนัสก่อนคืนยอดเสีย</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  หักยอดโบนัสที่ได้รับในวันนั้นออกก่อนคำนวณยอดเสีย
                </p>
              </div>
              <Switch checked={afterBonus} onCheckedChange={setAfterBonus} />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>คืนยอดเสียเป็นเงินโบนัส</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  คืนเป็นโบนัสแทนเงินสด (ต้องทำเทิร์น)
                </p>
              </div>
              <Switch checked={returnBonus} onCheckedChange={setReturnBonus} />
            </div>

            {returnBonus && (
              <div className="space-y-2">
                <Label>จำนวน Turn Over (X)</Label>
                <Input
                  type="number"
                  value={returnBonusTurnover}
                  onChange={(e) => setReturnBonusTurnover(Number(e.target.value))}
                  placeholder="1"
                />
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  อนุมัติและโอนเงินอัตโนมัติ
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  คืนยอดให้สมาชิกทันทีโดยไม่ต้อง review
                </p>
              </div>
              <Switch checked={autoApprove} onCheckedChange={setAutoApprove} />
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label className="flex items-center gap-2">
                  🔔 แจ้งเตือนสมาชิก
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  ส่งข้อความแจ้งเตือนไปยังกล่องข้อความของสมาชิกเมื่อคืนยอดสำเร็จ
                </p>
              </div>
              <Switch checked={notifyMember} onCheckedChange={setNotifyMember} />
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label>เปิดใช้งาน</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  เมื่อเปิดใช้งาน ระบบจะทำงานตามตารางเวลาที่กำหนด
                </p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !name || selectedGames.length === 0}>
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editConfig ? 'บันทึก' : 'สร้าง Config'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Preview Dialog Component
// ============================================

function PreviewDialog({
  open,
  onClose,
  config,
  onExecute,
}: {
  open: boolean
  onClose: () => void
  config: LossbackConfig | null
  onExecute: (configId: string, autoApprove: boolean) => void
}) {
  const { sessionToken } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [preview, setPreview] = useState<LossbackPreviewResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open && config && sessionToken) {
      loadPreview()
    }
  }, [open, config, sessionToken])

  const loadPreview = async () => {
    if (!config || !sessionToken) return
    
    setIsLoading(true)
    setError(null)
    try {
      const result = await AutoLossbackService.testConfig(sessionToken, config.objectId)
      setPreview(result)
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาด')
    } finally {
      setIsLoading(false)
    }
  }

  if (!config) return null

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center">
              <Eye className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <span className="text-lg">Preview ก่อนรัน</span>
              <DialogDescription className="mt-1">
                {config.name}
              </DialogDescription>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{error}</p>
              <Button variant="outline" className="mt-4" onClick={loadPreview}>
                ลองใหม่
              </Button>
            </div>
          ) : preview ? (
            <div className="space-y-4">
              {/* Config Info */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">วันที่คำนวณ:</span>
                  <span className="font-medium">{preview.dates.join(', ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">กลุ่มเกม:</span>
                  <span className="font-medium">{preview.game_groups.join(', ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">เปอร์เซ็นต์คืน:</span>
                  <span className="font-medium text-pink-600">{preview.percent_return}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ยอดเสียขั้นต่ำ:</span>
                  <span className="font-medium">฿{formatNumber(preview.amount_lose_min)}</span>
                </div>
              </div>

              {/* Preview Results */}
              <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <h4 className="font-medium text-emerald-600 mb-3">ผลการประมาณการ</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {formatNumber(preview.preview.estimated_member_count)}
                    </p>
                    <p className="text-xs text-muted-foreground">สมาชิก</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">
                      ฿{formatNumber(preview.preview.estimated_total_loss)}
                    </p>
                    <p className="text-xs text-muted-foreground">ยอดเสียรวม</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">
                      ฿{formatNumber(preview.preview.estimated_total_return)}
                    </p>
                    <p className="text-xs text-muted-foreground">ยอดคืนรวม</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                {preview.note}
              </p>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            ปิด
          </Button>
          <Button
            onClick={() => onExecute(config.objectId, config.auto_approve)}
            disabled={isLoading || !!error || !preview?.preview.estimated_member_count}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Play className="w-4 h-4 mr-2" />
            รันทันที
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Main Component
// ============================================

export function AutoLossbackPage() {
  const { sessionToken } = useAuth()

  // Config list state
  const [configs, setConfigs] = useState<LossbackConfig[]>([])
  const [stats, setStats] = useState<LossbackConfigStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Dialog state
  const [showConfigDialog, setShowConfigDialog] = useState(false)
  const [editConfig, setEditConfig] = useState<LossbackConfig | null>(null)
  const [showPreviewDialog, setShowPreviewDialog] = useState(false)
  const [previewConfig, setPreviewConfig] = useState<LossbackConfig | null>(null)

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<LossbackConfig | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Execute state
  const [, setIsExecuting] = useState<string | null>(null)

  // Load data
  const loadData = useCallback(async () => {
    if (!sessionToken) return

    setIsLoading(true)
    try {
      const [configsResult, statsResult] = await Promise.all([
        AutoLossbackService.getLossbackConfigs(sessionToken, { limit: 100 }),
        AutoLossbackService.getStats(sessionToken),
      ])
      setConfigs(configsResult.items)
      setStats(statsResult)
    } catch (err) {
      console.error('Error loading data:', err)
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Handlers
  const handleToggle = async (config: LossbackConfig) => {
    if (!sessionToken) return

    try {
      await AutoLossbackService.toggleConfig(sessionToken, config.objectId)
      loadData()
    } catch (err: any) {
      console.error('Error toggling config:', err)
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget?.objectId || !sessionToken) return

    setIsDeleting(true)
    try {
      await AutoLossbackService.deleteConfig(sessionToken, deleteTarget.objectId)
      setDeleteTarget(null)
      loadData()
    } catch (err: any) {
      console.error('Error deleting:', err)
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleExecute = async (configId: string, autoApprove: boolean) => {
    if (!sessionToken) return

    setIsExecuting(configId)
    try {
      const result = await AutoLossbackService.executeConfig(sessionToken, configId, { auto_approve: autoApprove })
      alert(`${result.message}\n\nสมาชิก: ${result.member_count} ราย\nยอดคืน: ฿${formatNumber(result.amount_return_count)}`)
      setShowPreviewDialog(false)
      loadData()
    } catch (err: any) {
      console.error('Error executing config:', err)
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    } finally {
      setIsExecuting(null)
    }
  }

  const handleCreateDefault = async () => {
    if (!sessionToken) return

    try {
      const result = await AutoLossbackService.createDefaultConfigs(sessionToken)
      alert(`สร้าง Config ใหม่ ${result.created.length} รายการ\nข้าม ${result.skipped.length} รายการที่มีอยู่แล้ว`)
      loadData()
    } catch (err: any) {
      console.error('Error creating defaults:', err)
      alert(`เกิดข้อผิดพลาด: ${err.message}`)
    }
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6 text-purple-500" />
            คืนยอดเสียอัตโนมัติ
          </h1>
          <p className="text-sm text-muted-foreground">
            ตั้งค่าระบบคืนยอดเสียอัตโนมัติ • {configs.length} รายการ
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={loadData}
            disabled={isLoading}
          >
            <RefreshCcw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button variant="outline" onClick={handleCreateDefault}>
            สร้าง Default
          </Button>
          <Button className="gap-2" onClick={() => { setEditConfig(null); setShowConfigDialog(true) }}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">สร้าง Config ใหม่</span>
            <span className="sm:hidden">สร้าง</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards stats={stats} isLoading={isLoading} />

      {/* Table - Configs */}
      <Card className="border-zinc-200/50 dark:border-zinc-800">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : configs.length === 0 ? (
            <div className="p-12 text-center">
              <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-sm text-muted-foreground">ไม่พบ Config คืนยอดเสียอัตโนมัติ</p>
              <p className="text-xs text-muted-foreground mt-1">กด "สร้าง Default" เพื่อสร้าง Config ตัวอย่าง</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>ชื่อ Config</TableHead>
                    <TableHead>กลุ่มเกม</TableHead>
                    <TableHead className="text-center">รอบ</TableHead>
                    <TableHead className="text-right">ขั้นต่ำ</TableHead>
                    <TableHead className="text-right">%</TableHead>
                    <TableHead className="text-center">Auto</TableHead>
                    <TableHead className="text-center">สถานะ</TableHead>
                    <TableHead>รันล่าสุด</TableHead>
                    <TableHead className="w-32"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configs.map((config, index) => (
                    <TableRow key={config.objectId} className="hover:bg-muted/50">
                      <TableCell className="text-muted-foreground text-sm">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{config.name}</p>
                          {config.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {config.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {config.game_groups.slice(0, 3).map((game) => (
                            <Badge key={game} variant="secondary" className="text-xs">
                              {game}
                            </Badge>
                          ))}
                          {config.game_groups.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{config.game_groups.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {getPeriodBadge(config.period)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        ฿{formatNumber(config.amount_lose_min)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-pink-600 font-bold">
                        {config.percent_return}%
                      </TableCell>
                      <TableCell className="text-center">
                        {config.auto_approve ? (
                          <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 gap-1">
                            <Zap className="w-3 h-3" />
                            Auto
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">
                            Manual
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={config.is_active}
                          onCheckedChange={() => handleToggle(config)}
                        />
                      </TableCell>
                      <TableCell>
                        {config.last_run_at ? (
                          <div className="text-xs">
                            <p>{formatDate(config.last_run_at)}</p>
                            {config.last_run_result && (
                              <p className="text-muted-foreground">
                                {config.last_run_result.member_count} ราย •
                                ฿{formatNumber(config.last_run_result.amount_return)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">ยังไม่เคยรัน</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setPreviewConfig(config); setShowPreviewDialog(true) }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Preview
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditConfig(config); setShowConfigDialog(true) }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setDeleteTarget(config)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-500 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-600">การทำงานอัตโนมัติ</p>
              <ul className="text-muted-foreground mt-1 space-y-1 list-disc list-inside">
                <li><strong>Daily:</strong> รันทุกวัน (คืนยอดเมื่อวาน)</li>
                <li><strong>Weekly:</strong> รันทุกวันจันทร์ (คืนยอด จ.-อา. ของสัปดาห์ก่อน)</li>
                <li><strong>Semi-Monthly:</strong> รันวันที่ 1 (คืนยอด 16-สิ้นเดือน) และวันที่ 16 (คืนยอด 1-15)</li>
                <li><strong>Monthly:</strong> รันวันที่ 1 (คืนยอดทั้งเดือนก่อน)</li>
              </ul>
              <p className="mt-2 text-xs">
                * ต้องเปิดใช้งาน Cron Job "processDailyLossback" และ "processWeeklyLossback" ใน Cron Jobs เพื่อให้ระบบทำงานอัตโนมัติ
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Config Dialog */}
      <ConfigDialog
        open={showConfigDialog}
        onClose={() => { setShowConfigDialog(false); setEditConfig(null) }}
        onSuccess={loadData}
        editConfig={editConfig}
      />

      {/* Preview Dialog */}
      <PreviewDialog
        open={showPreviewDialog}
        onClose={() => { setShowPreviewDialog(false); setPreviewConfig(null) }}
        config={previewConfig}
        onExecute={handleExecute}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              ยืนยันการลบ
            </AlertDialogTitle>
            <AlertDialogDescription>
              คุณต้องการลบ Config นี้หรือไม่?
              <div className="mt-3 p-3 bg-muted rounded-lg text-sm space-y-1">
                <p>ชื่อ: <strong className="text-foreground">{deleteTarget?.name}</strong></p>
                <p>รอบ: <strong className="text-foreground">{deleteTarget && getPeriodLabel(deleteTarget.period)}</strong></p>
                <p>เกม: <strong className="text-foreground">{deleteTarget?.game_groups?.join(', ')}</strong></p>
              </div>
              <p className="mt-3 text-destructive font-medium">⚠️ การลบนี้ไม่สามารถย้อนกลับได้</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleDelete()
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              ยืนยันลบ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
